import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Sparkles, Star, Heart, User, ArrowLeft, 
  RefreshCw, Loader2, MessageCircle 
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompatibilityBar = ({ label, value, delay }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay }}
    className="space-y-2"
  >
    <div className="flex justify-between text-sm">
      <span className="text-[#4A453E]">{label}</span>
      <span className="text-[#D4AF37] font-medium">{value}%</span>
    </div>
    <div className="compatibility-bar">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${value}%` }}
        transition={{ delay: delay + 0.2, duration: 0.8 }}
        className="compatibility-fill"
      />
    </div>
  </motion.div>
);

export const MatchDetailPage = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reading, setReading] = useState(null);
  const [loadingReading, setLoadingReading] = useState(false);

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        const response = await axios.get(`${API}/matches/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMatch(response.data);
      } catch (error) {
        console.error('Error fetching match:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMatch();
  }, [id, token]);

  const generateReading = async () => {
    if (!user || !match) return;
    setLoadingReading(true);
    try {
      const response = await axios.post(`${API}/reading`, {
        person1_name: user.full_name,
        person1_birth_date: user.birth_date,
        person2_name: match.user.full_name,
        person2_birth_date: match.user.birth_date
      });
      setReading(response.data.reading);
    } catch (error) {
      console.error('Error generating reading:', error);
    } finally {
      setLoadingReading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-[#8C857B]">Loading cosmic connection...</p>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#8C857B]">Match not found</p>
          <Link to="/matches" className="text-[#D4AF37] mt-4 inline-block">
            Back to Matches
          </Link>
        </div>
      </div>
    );
  }

  const radarData = [
    { subject: 'Life Path', A: match.compatibility.life_path, fullMark: 100 },
    { subject: 'Expression', A: match.compatibility.expression, fullMark: 100 },
    { subject: 'Soul Urge', A: match.compatibility.soul_urge, fullMark: 100 },
    { subject: 'Personality', A: match.compatibility.personality, fullMark: 100 },
    { subject: 'Birthday', A: match.compatibility.birthday, fullMark: 100 },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-xl border-b border-[#E8E2D2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="detail-logo">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-2xl text-[#2D2A26]">Intuitive Matchmaker</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <Link 
          to="/matches" 
          className="inline-flex items-center gap-2 text-[#8C857B] hover:text-[#D4AF37] mb-8 transition-colors"
          data-testid="back-to-matches"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Matches</span>
        </Link>

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="ethereal-card rounded-3xl p-8 md:p-10 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center text-white font-heading text-4xl glow-gold">
              {match.user.full_name?.charAt(0)}
            </div>
            <div className="text-center md:text-left flex-1">
              <h1 className="font-heading text-3xl md:text-4xl text-[#2D2A26] mb-2">
                {match.user.full_name}
              </h1>
              <p className="text-[#8C857B] mb-4">
                Seeking {match.user.looking_for === 'love' ? 'Romantic Love' : 
                        match.user.looking_for === 'friendship' ? 'Kindred Spirits' :
                        match.user.looking_for === 'guidance' ? 'Life Guidance' : 'Spiritual Connection'}
              </p>
              {match.user.bio && (
                <p className="text-[#4A453E] font-light italic max-w-xl">
                  "{match.user.bio}"
                </p>
              )}
            </div>
            <div className="text-center">
              <div className="text-5xl font-heading text-gold-gradient mb-2">
                {match.compatibility.overall}%
              </div>
              <p className="text-[#8C857B] text-sm mb-3">Overall Compatibility</p>
              <Link to={`/messages/${match.user.id}`} data-testid="message-user-btn">
                <Button className="rounded-full btn-gold">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Compatibility Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="ethereal-card rounded-2xl p-6"
          >
            <h2 className="font-heading text-xl text-[#2D2A26] mb-6">Compatibility Chart</h2>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E8E2D2" />
                  <PolarAngleAxis 
                    dataKey="subject" 
                    tick={{ fill: '#4A453E', fontSize: 12 }}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 100]} 
                    tick={{ fill: '#8C857B', fontSize: 10 }}
                  />
                  <Radar
                    name="Compatibility"
                    dataKey="A"
                    stroke="#D4AF37"
                    fill="#D4AF37"
                    fillOpacity={0.3}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Compatibility Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="ethereal-card rounded-2xl p-6"
          >
            <h2 className="font-heading text-xl text-[#2D2A26] mb-6">Compatibility Breakdown</h2>
            <div className="space-y-5">
              <CompatibilityBar label="Life Path" value={match.compatibility.life_path} delay={0.3} />
              <CompatibilityBar label="Expression" value={match.compatibility.expression} delay={0.4} />
              <CompatibilityBar label="Soul Urge" value={match.compatibility.soul_urge} delay={0.5} />
              <CompatibilityBar label="Personality" value={match.compatibility.personality} delay={0.6} />
              <CompatibilityBar label="Birthday" value={match.compatibility.birthday} delay={0.7} />
            </div>
          </motion.div>
        </div>

        {/* Numerology Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="ethereal-card rounded-2xl p-6 mb-8"
        >
          <h2 className="font-heading text-xl text-[#2D2A26] mb-6">Numerology Comparison</h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-[#8C857B] text-sm mb-2">You</p>
            </div>
            <div>
              <p className="text-[#D4AF37] text-sm mb-2">Number</p>
            </div>
            <div>
              <p className="text-[#8C857B] text-sm mb-2">{match.user.full_name?.split(' ')[0]}</p>
            </div>
            
            {[
              { name: 'Life Path', icon: Star, key: 'life_path' },
              { name: 'Expression', icon: Sparkles, key: 'expression' },
              { name: 'Soul Urge', icon: Heart, key: 'soul_urge' },
              { name: 'Personality', icon: User, key: 'personality' },
            ].map((item) => (
              <React.Fragment key={item.key}>
                <div className="number-circle w-12 h-12 text-lg mx-auto">
                  {user?.numerology?.[item.key]}
                </div>
                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-2 text-[#4A453E]">
                    <item.icon className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-sm">{item.name}</span>
                  </div>
                </div>
                <div className="number-circle w-12 h-12 text-lg mx-auto">
                  {match.user.numerology?.[item.key]}
                </div>
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* AI Reading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="ethereal-card rounded-2xl p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl text-[#2D2A26]">Cosmic Reading</h2>
            <Button
              onClick={generateReading}
              disabled={loadingReading}
              className="rounded-full btn-gold"
              data-testid="generate-reading-btn"
            >
              {loadingReading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Channeling...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {reading ? 'New Reading' : 'Generate Reading'}
                </>
              )}
            </Button>
          </div>

          {reading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="prose prose-lg max-w-none"
            >
              <div className="text-[#4A453E] font-light leading-relaxed whitespace-pre-line font-accent italic text-lg">
                {reading}
              </div>
            </motion.div>
          ) : (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 text-[#D4AF37]/30 mx-auto mb-4" />
              <p className="text-[#8C857B]">
                Click "Generate Reading" to receive a personalized cosmic compatibility reading
              </p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default MatchDetailPage;
